// Shared helpers for seeding the create-promo drawer from a recommendation's LEGACY
// (non-pilot) promo_object. Pilot hotels go through derivedRatesMapper instead.
//
// Extracted from PromoCard so the Action Centre v2 cards (LlmActionCard, EO-1217) can
// reuse the same non-pilot path rather than re-implementing the type coercion.

// Coerce a string value to match the template value's type (boolean or number)
const coerceType = (templateVal, overrideVal) => {
  if (typeof overrideVal !== "string") return overrideVal;
  if (typeof templateVal === "boolean") return overrideVal === "true";
  if (typeof templateVal === "number") {
    const num = Number.parseFloat(overrideVal);
    return Number.isNaN(num) ? overrideVal : num;
  }
  return overrideVal;
};

const isPlainObject = (val) =>
  val !== null && typeof val === "object" && !Array.isArray(val);

// Deep merge promoObject into PROMO_OBJECT template, coercing types based on the template.
export const deepMergeWithTypes = (template, override) => {
  if (override === null || override === undefined) return template;
  if (template === null || template === undefined) return override;
  if (!isPlainObject(template) || !isPlainObject(override)) return override;

  const result = { ...template };
  for (const key of Object.keys(override)) {
    const tVal = template[key];
    const oVal = override[key];

    if (isPlainObject(tVal) && isPlainObject(oVal)) {
      result[key] = deepMergeWithTypes(tVal, oVal);
    } else {
      result[key] = coerceType(tVal, oVal);
    }
  }
  return result;
};

const DAY_NAME_TO_NUMBER = {
  SUNDAY: 1,
  MONDAY: 2,
  TUESDAY: 3,
  WEDNESDAY: 4,
  THURSDAY: 5,
  FRIDAY: 6,
  SATURDAY: 7,
};

const toInt = (v) => (v != null && v !== "" ? Number.parseInt(v, 10) : null);
const toFloat = (v) => (v != null && v !== "" ? Number.parseFloat(v) : null);
const toBool = (v) => {
  if (typeof v === "boolean") return v;
  if (v === "true") return true;
  if (v === "false") return false;
  return v;
};
const toFloatMap = (map) => {
  if (!map || typeof map !== "object") return map;
  const result = {};
  for (const key of Object.keys(map)) {
    result[key] = toFloat(map[key]) ?? 0;
  }
  return result;
};
const toDayNumbers = (arr) => {
  if (!Array.isArray(arr)) return arr;
  return arr.map((v) =>
    typeof v === "string" ? (DAY_NAME_TO_NUMBER[v.toUpperCase()] ?? v) : v
  );
};

// Ensure all ruleVO fields match the backend DTO types. Mutates in place.
export const sanitizeRuleVO = (ruleVO) => {
  if (!ruleVO) return;

  const booking = ruleVO.bookingVO;
  if (booking) {
    booking.minBookingLeadTime = toInt(booking.minBookingLeadTime);
    booking.maxBookingLeadTime = toInt(booking.maxBookingLeadTime);
    booking.bookingWindowChoice = toInt(booking.bookingWindowChoice);
    booking.recurBkgRuleEveryN = toInt(booking.recurBkgRuleEveryN);
    booking.recurBkgRuleTimePeriodHours = toInt(
      booking.recurBkgRuleTimePeriodHours
    );
    booking.recurBkgRuleTimePeriodMinutes = toInt(
      booking.recurBkgRuleTimePeriodMinutes
    );
    booking.recurBkgRulePatternSpan = toInt(booking.recurBkgRulePatternSpan);
    booking.recurBkgRulePatternType = toInt(booking.recurBkgRulePatternType);
    booking.recurBkgRulePatternMonthDayNofMonth = toInt(
      booking.recurBkgRulePatternMonthDayNofMonth
    );
    booking.recurBkgRulePatternMonthSelect1 = toInt(
      booking.recurBkgRulePatternMonthSelect1
    );
    booking.recurBkgRulePatternMonthSelect2 = toInt(
      booking.recurBkgRulePatternMonthSelect2
    );
    booking.recurBkgRuleEndpoint = toInt(booking.recurBkgRuleEndpoint);
    booking.recurBkgRuleEndAfterNrecurs = toInt(
      booking.recurBkgRuleEndAfterNrecurs
    );
    booking.recurBkgRuleStartHours = toInt(booking.recurBkgRuleStartHours);
    booking.recurBkgRuleStartMins = toInt(booking.recurBkgRuleStartMins);
    booking.recurBkgRuleDurationHour = toInt(booking.recurBkgRuleDurationHour);
    booking.recurBkgRuleDurationMins = toInt(booking.recurBkgRuleDurationMins);
    booking.recurBkgRulePatternWeekDaysOfWeek = toDayNumbers(
      booking.recurBkgRulePatternWeekDaysOfWeek
    );
  }

  const discount = ruleVO.discountVO;
  if (discount) {
    discount.mCompDiscountType = toInt(discount.mCompDiscountType);
    discount.discountType = toInt(discount.discountType);
    discount.steadyDiscount = toBool(discount.steadyDiscount);
    discount.nthNightDiscountIsRecurring = toBool(
      discount.nthNightDiscountIsRecurring
    );
    discount.numSteadyDaysToDiscount = toInt(discount.numSteadyDaysToDiscount);
    discount.nthNightToDiscount = toInt(discount.nthNightToDiscount);
    discount.appliedOnType = toInt(discount.appliedOnType);
    discount.appliedOnNumNights = toInt(discount.appliedOnNumNights);
    discount.fixedDiscountAmount = toFloat(discount.fixedDiscountAmount);
    discount.percentDiscountAmount = toFloat(discount.percentDiscountAmount);
    discount.fixedDiscountMap = toFloatMap(discount.fixedDiscountMap);
    discount.percentDiscountMap = toFloatMap(discount.percentDiscountMap);
    discount.appliedDaysOfWeek = toDayNumbers(discount.appliedDaysOfWeek);
  }

  const stay = ruleVO.stayVO;
  if (stay) {
    stay.mustIncludeAny = toBool(stay.mustIncludeAny);
    stay.checkInStayThruEnabled = toBool(stay.checkInStayThruEnabled);
    stay.checkOutStayThruEnabled = toBool(stay.checkOutStayThruEnabled);
    stay.minLengthOfStay = toInt(stay.minLengthOfStay);
    stay.maxLengthOfStay = toInt(stay.maxLengthOfStay);
    stay.mustArriveDaysOfWeek = toDayNumbers(stay.mustArriveDaysOfWeek);
    stay.mustIncludeOneDaysOfWeek = toDayNumbers(stay.mustIncludeOneDaysOfWeek);
  }

  const geoRadius = ruleVO.geoRadiusVO;
  if (geoRadius) {
    geoRadius.radius = toFloat(geoRadius.radius) ?? 0;
  }
};
